import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { repositorioEstoqueItem } from '../../sync/repositories/almoxarifado';
import { extrairMensagensErro } from '../../utils/errorUtils';

const TIPOS_ITEM = [
  { value: 'insumo', label: 'Insumo Agrícola/Pecuário' },
  { value: 'produto_final', label: 'Produto Final (Venda)' },
  { value: 'ativo', label: 'Ativo Imobilizado/Ferramenta' },
];

/**
 * Modal de cadastro rápido de Item de Estoque.
 *
 * Props:
 *   isOpen, onClose, onSuccess(novoItem)
 *   unidadesMedida — lista [{id, sigla, descricao}] carregada pela página pai
 *   (Unidade de Medida é obrigatória na API; se não houver nenhuma
 *   cadastrada, o cadastro fica bloqueado até existir uma).
 */
export default function ModalCadastroItemEstoque({ isOpen, onClose, onSuccess, unidadesMedida = [] }) {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('insumo');
  const [unidadeMedida, setUnidadeMedida] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setNome('');
      setTipo('insumo');
      setUnidadeMedida('');
      setErros([]);
      setSalvando(false);
    }
  }, [isOpen]);

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!nome.trim()) { setErros(['O nome é obrigatório.']); return; }
    if (!unidadeMedida) { setErros(['Selecione a unidade de medida.']); return; }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioEstoqueItem.criar({
        nome: nome.trim(),
        tipo,
        unidade_medida_id: unidadeMedida,
        saldo_atual: 0,
        custo_unitario: 0,
        preco_venda: 0,
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Item de Estoque">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
            placeholder="Ex: Ureia, Óleo Diesel S10..."
            autoFocus
            disabled={salvando}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            disabled={salvando}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
          >
            {TIPOS_ITEM.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Unidade de Medida *</label>
          {unidadesMedida.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhuma unidade de medida cadastrada. Cadastre uma na tela de Unidades de Medida antes de continuar.
            </p>
          ) : (
            <select
              value={unidadeMedida}
              onChange={(e) => setUnidadeMedida(e.target.value)}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {unidadesMedida.map((u) => (
                <option key={u.id} value={u.id}>{u.sigla} - {u.descricao}</option>
              ))}
            </select>
          )}
        </div>

        <p className="text-sm text-gray-600">
          Saldo, custo e preço de venda ficam zerados — complete na tela de Itens de Estoque depois.
        </p>

        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando || unidadesMedida.length === 0}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
